<template>
  <div class="md-layout currency-page">
    <div class="md-layout-item">
      <md-card class="main-card">
        <!-- Header -->
        <md-card-header class="card-header">
          <div class="header-left">
            <div class="card-icon"><md-icon>attach_money</md-icon></div>
            <div>
              <h4 class="title">Currencies</h4>
              <span class="subtitle"
                >{{ tableData.length }} currency{{
                  tableData.length !== 1 ? "ies" : ""
                }}
                configured</span
              >
            </div>
          </div>
          <md-button class="md-raised add-btn" @click="openCreate">
            <md-icon>add</md-icon> Add Currency
          </md-button>
        </md-card-header>

        <!-- Table -->
        <md-card-content class="table-content">
          <div v-if="loading" class="state-box">
            <md-progress-spinner
              md-mode="indeterminate"
              :md-diameter="36"
              :md-stroke="3"
            />
            <p>Loading…</p>
          </div>

          <div v-else-if="tableData.length === 0" class="state-box">
            <md-icon class="empty-icon">currency_exchange</md-icon>
            <p>No currencies yet</p>
            <small>Add your first currency to get started.</small>
          </div>

          <table v-else class="data-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Symbol</th>
                <th>Position</th>
                <th>Decimals</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in tableData" :key="item._id" class="data-row">
                <td>
                  <span class="code-badge">{{ item.code }}</span>
                </td>
                <td class="name-cell">{{ item.name }}</td>
                <td>
                  <span class="symbol-display">{{ item.symbol }}</span>
                </td>
                <td class="text-muted small">{{ item.symbolPosition }}</td>
                <td class="text-muted small">{{ item.decimalPlaces }}</td>
                <td>
                  <span
                    class="status-badge"
                    :class="item.isActive ? 'active' : 'inactive'"
                  >
                    {{ item.isActive ? "Active" : "Inactive" }}
                  </span>
                </td>
                <td>
                  <div class="actions-row">
                    <md-button
                      class="md-just-icon md-simple action-btn btn-warning"
                      title="Edit"
                      @click="openEdit(item)"
                    >
                      <md-icon>edit</md-icon>
                    </md-button>
                    <md-button
                      class="md-just-icon md-simple action-btn"
                      :class="item.isActive ? 'btn-muted' : 'btn-success'"
                      :title="item.isActive ? 'Deactivate' : 'Activate'"
                      @click="toggleActive(item)"
                    >
                      <md-icon>{{
                        item.isActive ? "toggle_on" : "toggle_off"
                      }}</md-icon>
                    </md-button>
                    <md-button
                      class="md-just-icon md-simple action-btn btn-danger"
                      title="Delete"
                      @click="deleteCurrency(item)"
                    >
                      <md-icon>delete_outline</md-icon>
                    </md-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </md-card-content>
      </md-card>
    </div>

    <!-- Create / Edit Modal -->
    <transition name="modal-fade">
      <div v-if="showModal" class="modal-overlay" @click.self="closeModal">
        <div class="modal-box">
          <div class="modal-header">
            <h5>{{ editMode ? "Edit Currency" : "Add Currency" }}</h5>
            <button class="modal-close" @click="closeModal">
              <md-icon>close</md-icon>
            </button>
          </div>

          <div class="modal-body">
            <div class="form-row two-col">
              <div class="form-group">
                <label class="field-label"
                  >Code <span class="req">*</span></label
                >
                <input
                  v-model="form.code"
                  class="field-input"
                  placeholder="GBP"
                  maxlength="5"
                  :disabled="editMode"
                />
                <small class="hint">ISO 4217 code (e.g. GBP, EUR, USD)</small>
              </div>
              <div class="form-group">
                <label class="field-label"
                  >Symbol <span class="req">*</span></label
                >
                <input
                  v-model="form.symbol"
                  class="field-input"
                  placeholder="£"
                  maxlength="5"
                />
              </div>
            </div>

            <div class="form-group">
              <label class="field-label">Name <span class="req">*</span></label>
              <input
                v-model="form.name"
                class="field-input"
                placeholder="British Pound"
              />
            </div>

            <div class="form-row two-col">
              <div class="form-group">
                <label class="field-label">Symbol Position</label>
                <select
                  v-model="form.symbolPosition"
                  class="field-input field-select"
                >
                  <option value="before">Before (£10.00)</option>
                  <option value="after">After (10.00£)</option>
                </select>
              </div>
              <div class="form-group">
                <label class="field-label">Decimal Places</label>
                <input
                  v-model.number="form.decimalPlaces"
                  type="number"
                  min="0"
                  max="4"
                  class="field-input"
                />
              </div>
            </div>

            <div class="form-row two-col">
              <div class="form-group">
                <label class="field-label">Thousand Separator</label>
                <input
                  v-model="form.thousandSeparator"
                  class="field-input"
                  placeholder=","
                  maxlength="1"
                />
              </div>
              <div class="form-group">
                <label class="field-label">Decimal Separator</label>
                <input
                  v-model="form.decimalSeparator"
                  class="field-input"
                  placeholder="."
                  maxlength="1"
                />
              </div>
            </div>

            <!-- Preview -->
            <div class="preview-box">
              <span class="preview-label">Preview:</span>
              <span class="preview-value">{{ formatPreview }}</span>
            </div>

            <div class="form-group toggle-group">
              <label class="field-label">Active</label>
              <div class="toggle-row">
                <md-switch v-model="form.isActive" />
                <span class="toggle-text">{{
                  form.isActive ? "Active" : "Inactive"
                }}</span>
              </div>
            </div>
          </div>

          <div class="modal-footer">
            <button class="btn-cancel" @click="closeModal">Cancel</button>
            <button class="btn-save" @click="saveForm" :disabled="saving">
              <i v-if="saving" class="fa fa-spinner fa-spin"></i>
              <md-icon v-else>check</md-icon>
              {{ editMode ? "Save Changes" : "Create Currency" }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import ProductService from "../../services/SuperAdmin/Kids/ProductService";
import Swal from "sweetalert2";

export default {
  data() {
    return {
      loading: false,
      saving: false,
      showModal: false,
      editMode: false,
      tableData: [],
      form: this.defaultForm(),
    };
  },
  computed: {
    formatPreview() {
      const amount = (1234.56)
        .toFixed(this.form.decimalPlaces || 2)
        .replace(".", "__DEC__")
        .replace(/\B(?=(\d{3})+(?!\d))/g, this.form.thousandSeparator || ",")
        .replace("__DEC__", this.form.decimalSeparator || ".");
      return this.form.symbolPosition === "after"
        ? `${amount}${this.form.symbol || ""}`
        : `${this.form.symbol || ""}${amount}`;
    },
  },
  methods: {
    defaultForm() {
      return {
        code: "",
        name: "",
        symbol: "",
        symbolPosition: "before",
        decimalPlaces: 2,
        thousandSeparator: ",",
        decimalSeparator: ".",
        isActive: true,
      };
    },
    openCreate() {
      this.form = this.defaultForm();
      this.editMode = false;
      this.showModal = true;
    },
    openEdit(item) {
      this.form = {
        _id: item._id,
        code: item.code,
        name: item.name,
        symbol: item.symbol,
        symbolPosition: item.symbolPosition,
        decimalPlaces: item.decimalPlaces,
        thousandSeparator: item.thousandSeparator,
        decimalSeparator: item.decimalSeparator,
        isActive: item.isActive,
      };
      this.editMode = true;
      this.showModal = true;
    },
    closeModal() {
      this.showModal = false;
    },
    async saveForm() {
      if (!this.form.code || !this.form.name || !this.form.symbol) {
        this.$toast.error("Code, Name and Symbol are required.");
        return;
      }
      this.saving = true;
      try {
        if (this.editMode) {
          await ProductService.updateCurrency(this.form);
          this.$toast.success("Currency updated.");
        } else {
          await ProductService.createCurrency(this.form);
          this.$toast.success("Currency created.");
        }
        this.closeModal();
        this.loadData();
      } catch (e) {
        this.$toast.error(
          e.response?.data?.respones_msg || "Operation failed."
        );
      } finally {
        this.saving = false;
      }
    },
    async toggleActive(item) {
      try {
        await ProductService.toggleCurrencyActive({
          _id: item._id,
          isActive: !item.isActive,
        });
        this.$toast.success(
          `Currency ${item.isActive ? "deactivated" : "activated"}.`
        );
        this.loadData();
      } catch (e) {
        this.$toast.error(e.response?.data?.respones_msg || "Failed.");
      }
    },
    deleteCurrency(item) {
      Swal.fire({
        title: "Delete currency?",
        text: `${item.name} (${item.code}) will be removed.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#f44336",
        confirmButtonText: "Delete",
      }).then(async (result) => {
        if (result.value) {
          try {
            await ProductService.deleteCurrency({ id: item._id });
            this.$toast.success("Currency deleted.");
            this.loadData();
          } catch (e) {
            this.$toast.error(
              e.response?.data?.respones_msg || "Delete failed."
            );
          }
        }
      });
    },
    loadData() {
      this.loading = true;
      ProductService.currencyList()
        .then((r) => {
          this.tableData = r.data.data;
        })
        .catch((e) => {
          this.$toast.error(
            e.response?.data?.respones_msg || "Failed to load."
          );
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  beforeMount() {
    this.loadData();
  },
};
</script>

<style scoped>
.currency-page {
  font-family: "Segoe UI", sans-serif;
}
.main-card {
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
}

/* Header */
.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px 16px;
  background: linear-gradient(135deg, #1976d2 0%, #0d47a1 100%);
  color: white;
}
.header-left {
  display: flex;
  align-items: center;
  gap: 14px;
}
.card-icon {
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.card-icon .md-icon {
  color: white !important;
}
.title {
  color: white;
  margin: 0;
  font-size: 18px;
  font-weight: 600;
}
.subtitle {
  color: rgba(255, 255, 255, 0.75);
  font-size: 13px;
}
.add-btn {
  background: rgba(255, 255, 255, 0.18) !important;
  color: white !important;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.3);
}

/* Table */
.table-content {
  padding: 0;
}
.data-table {
  width: 100%;
  border-collapse: collapse;
}
.data-table thead tr {
  background: #f5f5f5;
}
.data-table th {
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #666;
  border-bottom: 2px solid #e0e0e0;
}
.data-table td {
  padding: 12px 16px;
  font-size: 14px;
  border-bottom: 1px solid #f0f0f0;
  vertical-align: middle;
}
.data-row:hover td {
  background: #f8f9ff;
}
.text-muted {
  color: #888;
}
.small {
  font-size: 12px;
}

.code-badge {
  background: #e3f2fd;
  color: #1565c0;
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  font-family: monospace;
  letter-spacing: 1px;
}
.symbol-display {
  font-size: 18px;
  font-weight: 700;
  color: #333;
}
.status-badge {
  padding: 3px 10px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
}
.status-badge.active {
  background: #e8f5e9;
  color: #2e7d32;
}
.status-badge.inactive {
  background: #fce4ec;
  color: #c62828;
}

/* Actions */
.actions-row {
  display: flex;
  gap: 2px;
}
.action-btn.md-just-icon {
  width: 32px;
  height: 32px;
  min-width: 32px;
  border-radius: 8px !important;
}
.action-btn.md-just-icon .md-icon {
  font-size: 17px !important;
}
.action-btn.btn-warning .md-icon {
  color: #f57c00 !important;
}
.action-btn.btn-danger .md-icon {
  color: #c62828 !important;
}
.action-btn.btn-success .md-icon {
  color: #2e7d32 !important;
}
.action-btn.btn-muted .md-icon {
  color: #888 !important;
}

/* State boxes */
.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #aaa;
  text-align: center;
}
.empty-icon {
  font-size: 48px !important;
  color: #ddd !important;
  margin-bottom: 12px;
}

/* Modal */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}
.modal-box {
  background: white;
  border-radius: 14px;
  width: 100%;
  max-width: 520px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 24px;
  background: linear-gradient(135deg, #1976d2, #0d47a1);
  color: white;
}
.modal-header h5 {
  margin: 0;
  font-size: 16px;
  font-weight: 600;
  color: white;
}
.modal-close {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 6px;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}
.modal-close .md-icon {
  color: white !important;
  font-size: 16px !important;
}
.modal-body {
  padding: 24px;
}
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 16px 24px;
  border-top: 1px solid #f0f0f0;
}

/* Form */
.form-row {
  display: flex;
  gap: 16px;
}
.form-row.two-col > * {
  flex: 1;
}
.form-group {
  margin-bottom: 16px;
}
.field-label {
  display: block;
  font-size: 11px;
  font-weight: 700;
  color: #555;
  margin-bottom: 5px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}
.req {
  color: #e53935;
}
.hint {
  font-size: 11px;
  color: #aaa;
  display: block;
  margin-top: 3px;
}
.field-input {
  width: 100%;
  padding: 9px 12px;
  border: 1.5px solid #e0e0e0;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  background: white;
}
.field-input:focus {
  border-color: #1976d2;
}
.field-input:disabled {
  background: #f5f5f5;
  color: #888;
}
.field-select {
  -webkit-appearance: none;
  cursor: pointer;
}

.preview-box {
  background: #f0f4ff;
  border: 1px solid #c5cae9;
  border-radius: 8px;
  padding: 10px 14px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 10px;
}
.preview-label {
  font-size: 11px;
  font-weight: 700;
  color: #666;
  text-transform: uppercase;
}
.preview-value {
  font-size: 20px;
  font-weight: 700;
  color: #1976d2;
}

.toggle-group {
  display: flex;
  flex-direction: column;
}
.toggle-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.toggle-text {
  font-size: 13px;
  color: #555;
}

.btn-cancel {
  padding: 9px 20px;
  border-radius: 8px;
  border: 1.5px solid #e0e0e0;
  background: white;
  color: #666;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
}
.btn-save {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 9px 20px;
  border-radius: 8px;
  border: none;
  background: #1976d2;
  color: white;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;
}
.btn-save:hover:not(:disabled) {
  background: #1565c0;
}
.btn-save:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}
.btn-save .md-icon {
  color: white !important;
  font-size: 17px !important;
}

/* Transitions */
.modal-fade-enter-active,
.modal-fade-leave-active {
  transition: opacity 0.2s;
}
.modal-fade-enter,
.modal-fade-leave-to {
  opacity: 0;
}
</style>
